import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil, shareReplay, debounceTime, distinctUntilChanged } from 'rxjs/operators';

interface CacheItem<T> {
    data: T;
    timestamp: number;
    ttl: number; // Time to live in milliseconds
}

@Injectable({
    providedIn: 'root'
})
export class PerformanceService {
    private cache = new Map<string, CacheItem<any>>();
    private destroy$ = new Subject<void>();
    private memoryUsage$ = new BehaviorSubject<number>(0);

    // Default cache TTL (5 minutes)
    private readonly DEFAULT_TTL = 5 * 60 * 1000;

    constructor() {
        // Monitor memory usage
        this.startMemoryMonitoring();
    }

    /**
     * Cache data with TTL
     */
    setCache<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            ttl
        });
    }

    /**
     * Get cached data if not expired
     */
    getCache<T>(key: string): T | null {
        const item = this.cache.get(key);
        if (!item) return null;

        const isExpired = Date.now() - item.timestamp > item.ttl;
        if (isExpired) {
            this.cache.delete(key);
            return null;
        }

        return item.data;
    }

    /**
     * Clear specific cache item
     */
    clearCache(key: string): void {
        this.cache.delete(key);
    }

    /**
     * Clear all cache
     */
    clearAllCache(): void {
        this.cache.clear();
    }

    /**
     * Clear expired cache items
     */
    clearExpiredCache(): void {
        const now = Date.now();
        for (const [key, item] of this.cache.entries()) {
            if (now - item.timestamp > item.ttl) {
                this.cache.delete(key);
            }
        }
    }

    /**
     * Get cache statistics
     */
    getCacheStats(): { size: number; keys: string[] } {
        return {
            size: this.cache.size,
            keys: Array.from(this.cache.keys())
        };
    }

    /**
     * Debounced function wrapper
     */
    debounce<T>(fn: (...args: any[]) => T, delay: number = 300): (...args: any[]) => void {
        let timeoutId: NodeJS.Timeout;
        return (...args: any[]) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => fn(...args), delay);
        };
    }

    /**
     * Throttled function wrapper
     */
    throttle<T>(fn: (...args: any[]) => T, delay: number = 300): (...args: any[]) => void {
        let lastCall = 0;
        return (...args: any[]) => {
            const now = Date.now();
            if (now - lastCall >= delay) {
                lastCall = now;
                fn(...args);
            }
        };
    }

    /**
     * Optimized scroll handler
     */
    createScrollHandler(callback: (event: Event) => void, throttleMs: number = 16): (event: Event) => void {
        return this.throttle(callback, throttleMs);
    }

    /**
     * Optimized resize handler
     */
    createResizeHandler(callback: (event: Event) => void, debounceMs: number = 250): (event: Event) => void {
        return this.debounce(callback, debounceMs);
    }

    /**
     * Memory usage monitoring
     */
    private startMemoryMonitoring(): void {
        if ('memory' in performance) {
            setInterval(() => {
                const memory = (performance as any).memory;
                const usage = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
                this.memoryUsage$.next(usage);
            }, 10000); // Check every 10 seconds
        }
    }

    /**
     * Get memory usage observable
     */
    getMemoryUsage(): Observable<number> {
        return this.memoryUsage$.asObservable().pipe(
            takeUntil(this.destroy$),
            shareReplay(1)
        );
    }

    /**
     * Check if memory usage is high
     */
    isMemoryUsageHigh(): boolean {
        return this.memoryUsage$.value > 80; // 80% threshold
    }

    /**
     * Force garbage collection if available
     */
    forceGarbageCollection(): void {
        if ('gc' in window) {
            (window as any).gc();
        }
    }

    /**
     * Optimize images
     */
    optimizeImageUrl(url: string, width?: number, height?: number): string {
        if (!url) return url;

        // Add image optimization parameters
        const params = new URLSearchParams();
        if (width) params.append('w', width.toString());
        if (height) params.append('h', height.toString());
        params.append('q', '85'); // Quality 85%
        params.append('f', 'auto'); // Auto format

        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${params.toString()}`;
    }

    /**
     * Lazy load images
     */
    createLazyLoadObserver(callback: (entries: IntersectionObserverEntry[]) => void): IntersectionObserver {
        return new IntersectionObserver(callback, {
            rootMargin: '50px 0px',
            threshold: 0.1
        });
    }

    /**
     * Preload critical resources
     */
    preloadResource(url: string, type: 'script' | 'style' | 'image' = 'script'): void {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = url;
        link.as = type;
        document.head.appendChild(link);
    }

    /**
     * Cleanup on destroy
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.clearAllCache();
    }
} 